import React from 'react';

import { keyframes } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import { AtlassianIcon } from '@atlaskit/logo';
import { Main } from '@atlaskit/navigation-system';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import type { SpinnerProps } from '@atlaskit/spinner';

const loading = keyframes({
	'0%': { opacity: 0.7 },
	'50%': { opacity: 1 },
	'100%': { opacity: 0.7 },
});

const styles = cssMap({
	loadingSpinnerStyles: {
		height: '100%',
	},
	logoStyles: {
		animation: `${loading} 1.5s infinite`,
	},
});

type PageMainContentLoadingLogoProps = Pick<SpinnerProps, 'interactionName' | 'testId'>;

/**
 * A loading indicator component that displays an animated Atlassian logo.
 *
 * @remarks
 * This component is **only** intended for use as a loading state for a route page's main content area.
 * It should not be used for general-purpose loading indicators elsewhere in the application.
 */
export const PageMainContentLoadingLogo = ({
	interactionName,
	testId = 'loading-logo',
}: PageMainContentLoadingLogoProps) => (
	<UFOLoadHold name={interactionName ?? 'studio-loading-atlassian-logo'}>
		<Main>
			<Stack
				grow="fill"
				alignBlock="center"
				alignInline="center"
				xcss={styles.loadingSpinnerStyles}
				testId={testId}
			>
				<Box xcss={styles.logoStyles}>
					<AtlassianIcon appearance="brand" size="xlarge" />
				</Box>
			</Stack>
		</Main>
	</UFOLoadHold>
);
